import {
  useCreateGroupMutation,
  useUpdateGroupMutation,
} from '@/app/redux/endpoints/groups';
import { useAppSelector } from '@/app/redux/hooks';
import { useGroups } from '@/app/redux/hooks/groups';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import { Group } from '@/common/types/group';
import { Student } from '@/common/types/student';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { PlusIcon, WarningCircleIcon } from '@phosphor-icons/react';
import { Alert, AlertDescription } from '@shadcn/components/alert';
import { Button } from '@shadcn/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import { Input } from '@shadcn/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shadcn/components/select';
import { useState } from 'react';
import { toast } from 'sonner';
import styled from 'styled-components';

interface AddToGroupModalProps {
  open: boolean;
  onClose: () => void;
  students: Student[];
}

export default function AddToGroupModal({
  open,
  onClose,
  students,
}: AddToGroupModalProps) {
  const { currentOrganisationId } = useCurrentOrganisationId();
  const { groups, groupsById } = useGroups();

  const userId = useAppSelector(selectUserId);

  const [createGroupMutation] = useCreateGroupMutation();
  const [updateGroupMutation] = useUpdateGroupMutation();

  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<JSX.Element | string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [newGroupName, setNewGroupName] = useState('');

  async function handleCreateAndAddToGroup() {
    if (!userId || !currentOrganisationId) {
      console.error('User is missing');
      setError(UNKNOWN_ERROR_MESSAGE);
      return;
    }

    if (!newGroupName.trim()) {
      setError('Please enter a group name');
    }

    setLoading(true);
    try {
      const now = Date.now();
      const newGroup: Omit<Group, 'id'> = {
        name: newGroupName,
        organisationId: currentOrganisationId,
        managerIds: [userId],
        studentIds: students.map((s) => s.id),
        createdAt: now,
        updatedAt: now,
      };

      await createGroupMutation(newGroup).unwrap();

      setNewGroupName('');
      setSelectedGroup('');

      trackMixpanelEvent('Group created from add modal', {
        groupName: newGroupName,
        studentIds: students.map((s) => s.id),
      });

      toast.success('Group created and learners added');
      handleClose();
    } catch (error) {
      setError(UNKNOWN_ERROR_MESSAGE);
    }
    setLoading(false);
  }

  async function handleAddToGroup() {
    if (!userId) {
      setError(UNKNOWN_ERROR_MESSAGE);
      return;
    }

    if (selectedGroup === 'new') {
      await handleCreateAndAddToGroup();
      return;
    }

    const group = groupsById.get(selectedGroup);
    if (!group) {
      setError(
        'The selected group does not exist. Please select an existing group or create a new one.',
      );
      return;
    }

    setLoading(true);
    try {
      const updatedStudentIds = [
        ...new Set([...group.studentIds, ...students.map((s) => s.id)]),
      ];
      await updateGroupMutation({
        orgId: currentOrganisationId!,
        groupId: group.id,
        updates: { studentIds: updatedStudentIds },
      }).unwrap();
      trackMixpanelEvent('Learners added to group', {
        groupId: group.id,
        studentIds: updatedStudentIds,
      });
      toast.success('Learners added to group');
      handleClose();
    } catch (error) {
      console.error(error);
      setError(UNKNOWN_ERROR_MESSAGE);
    }
    setLoading(false);
  }

  function handleClose() {
    setError(null);
    setNewGroupName('');
    setSelectedGroup('');
    onClose();
  }

  return students.length ? (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>
            Add {students[0].studentFirstName} {students[0].studentSurname}{' '}
            {students.length > 1
              ? `+ ${students.length - 1} other${
                  students.length - 1 === 1 ? '' : 's'
                }`
              : ''}{' '}
            to group
          </DialogTitle>
          <DialogDescription>
            Select the group you want to add{' '}
            {students.length > 1 ? 'these students' : 'this student'} to
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {error && (
            <Alert variant="destructive">
              <WarningCircleIcon className="ml-2" size={16} />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <StyledSelect value={selectedGroup} onValueChange={setSelectedGroup}>
            <SelectTrigger>
              <SelectValue placeholder="Select a group..." />
            </SelectTrigger>
            <StyledSelectContent>
              {groups.map((group) => (
                <StyledSelectItem key={group.id} value={group.id!}>
                  {group.name}
                </StyledSelectItem>
              ))}
              <StyledSelectItem value="new">
                <span className="flex items-center gap-2">
                  <PlusIcon size={16} /> New Group
                </span>
              </StyledSelectItem>
            </StyledSelectContent>
          </StyledSelect>

          {selectedGroup === 'new' && (
            <Input
              placeholder="Enter group name..."
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleAddToGroup}
            disabled={
              selectedGroup === 'new' ? !newGroupName.trim() : !selectedGroup
            }
            loading={isLoading}
          >
            {selectedGroup === 'new' ? 'Create & Add' : 'Add to Group'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  ) : null;
}

const StyledSelect = styled(Select)`
  max-height: inherit;
`;

const StyledSelectContent = styled(SelectContent)`
  max-height: 250px;
  width: var(--radix-select-trigger-width);
  padding: 4px;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: hsl(var(--border));
    border-radius: 3px;
  }
`;

const StyledSelectItem = styled(SelectItem)`
  display: flex;
  align-items: center;
  width: calc(100% - 8px);
  gap: 8px;
  cursor: pointer;
  border-radius: 4px;
  &:hover {
    background-color: hsl(var(--accent));
  }
  &[data-selected='true'] {
    background-color: hsl(var(--accent));
  }
`;
